/**
 * Jigsaw puzzle engine — pure geometry + state (no DOM, no React).
 *
 * Coordinates are normalized to the board: x in board widths, y in board
 * heights. The play surface is the board plus a tray: below it ("stacked",
 * SURFACE_HEIGHT board heights tall) or beside it ("side", 1 + SIDE_TRAY_WIDTH
 * board widths wide, for short landscape screens). Resizing only rescales;
 * switching layout moves loose tray pieces with relayoutPuzzle() and never
 * touches placed pieces.
 *
 * Pieces are interlocking shapes: every interior edge is one tab/blank shared
 * by both neighbours (state.edges), outer edges are flat. A piece's logical
 * box is its grid cell; tabs overhang it by up to pieceOverhang() per side,
 * and every bound below (tray scatter, drag clamp) includes that overhang.
 * Loose pieces start below the tray's "Pieces" header band (TRAY_LABEL_BAND).
 * When the tray cannot hold its header plus one piece with overhang it grows
 * (surfaceBounds with a state); fitBoard() and the grid never depend on that growth.
 *
 * A "compact" puzzle (the player uses it on every viewport) gives every piece
 * its own tray slot, spaced by the piece's full box (tabs included) so no two
 * loose pieces overlap; the tray grows (stacked: taller, side: wider) until all
 * slots fit. A stacked tray may be wider than the board (state.trayWidth, board
 * widths): it is centred under the board, so the surface then starts left of
 * the board (surfaceOrigin) and x can be negative. The board and grid are the
 * same as without compact; only the tray changes.
 *
 * computeGrid() is mirrored by App\Services\Games\JigsawGrid (backend admin
 * preview); keep both in sync.
 */

export const DIFFICULTIES = ["easy", "medium", "hard"];

export const DEFAULT_PIECES = { easy: 12, medium: 36, hard: 80 };

export const MIN_SIDE = 2;
export const MAX_SIDE = 20;
export const MIN_PIECE_SOURCE_PX = 40;
export const MIN_PIECE_DISPLAY_PX = 36;
export const SURFACE_HEIGHT = 1.6;
export const SIDE_TRAY_WIDTH = 0.6;
export const SNAP_TOLERANCE = 0.3;
export const MAX_BOARD_WIDTH = 1100;
export const LAYOUTS = ["stacked", "side"];
/** Tab overhang per side, as a fraction of the piece's shorter side (tabs reach ≤ 0.23). */
export const TAB_OVERHANG = 0.24;
/** Gap between board and tray (board heights when stacked, board widths when side). */
export const TRAY_GAP = 0.04;
/** Phone viewports: portrait up to this width, or landscape up to this height. */
export const COMPACT_MAX_SIDE = 540;
/** Landscape phones are at most this wide (larger short windows keep the desktop tray). */
export const COMPACT_MAX_LANDSCAPE_WIDTH = 960;
/** Band (board heights) at the top of every tray for its "Pieces" header; loose pieces start below it. */
export const TRAY_LABEL_BAND = 0.1;

function normalizeLayout(layout) {
  return layout === "side" ? "side" : "stacked";
}

/** True on phone-sized viewports, where the tray gives every piece its own slot. */
export function isCompactViewport(viewportWidth, viewportHeight) {
  const w = Number(viewportWidth) || 0;
  const h = Number(viewportHeight) || 0;
  if (w <= 0 || h <= 0) return false;
  if (w <= COMPACT_MAX_SIDE) return true;
  return w > h && h <= COMPACT_MAX_SIDE && w <= COMPACT_MAX_LANDSCAPE_WIDTH;
}

/** Stacked tray width in board widths (at least the board's); side trays ignore it. */
function trayWidthOf(state, mode) {
  const w = state ? Number(state.trayWidth) : 0;
  return mode === "stacked" && w > 1 ? w : 1;
}

/** Top-left of the play surface in board units: left of the board when a stacked tray is wider than it. */
export function surfaceOrigin(state, layout) {
  const mode = normalizeLayout(layout === undefined && state ? state.layout : layout);
  return { x: -(trayWidthOf(state, mode) - 1) / 2, y: 0 };
}

/**
 * Compact tray packing: pieces per line along the tray (stacked: across the
 * tray width, side: down the board height), number of lines, and the tray
 * depth (board units) that holds them. Slots are one piece box (piece plus
 * tab overhang on both sides) apart in both directions, so boxes never overlap.
 * The label band sits above the first line (stacked) or line start (side).
 */
function trayPacking(state, layout) {
  const mode = normalizeLayout(layout);
  const size = pieceSize(state);
  const over = pieceOverhang(state);
  const n = state.rows * state.cols;
  const boxW = size.w + 2 * over.x;
  const boxH = size.h + 2 * over.y;
  const along = mode === "side" ? boxH : boxW;
  const across = mode === "side" ? boxW : boxH;
  const length = mode === "side" ? 1 - TRAY_LABEL_BAND : trayWidthOf(state, mode);
  const span = Math.max(0, length - along);
  const perLine = clamp(Math.floor(span / along + 1e-9) + 1, 1, n);
  const lines = Math.ceil(n / perLine);
  const depth = lines * across + (mode === "side" ? 0 : TRAY_LABEL_BAND);
  return { perLine, lines, depth };
}

/**
 * Play surface size in board units for a layout. With a puzzle state the tray
 * grows (stacked: taller, side: wider) until its header band plus one piece
 * with its tab overhang fits inside it (compact: until every slot fits).
 */
export function surfaceBounds(layout, state) {
  const mode = normalizeLayout(layout);
  const base =
    mode === "side"
      ? { width: 1 + SIDE_TRAY_WIDTH, height: 1 }
      : { width: trayWidthOf(state, mode), height: SURFACE_HEIGHT };
  if (!state) return base;
  if (state.compact) {
    const depth = 1 + TRAY_GAP + trayPacking(state, mode).depth;
    return mode === "side"
      ? { width: Math.max(base.width, depth), height: 1 }
      : { width: base.width, height: Math.max(base.height, depth) };
  }
  const size = pieceSize(state);
  const over = pieceOverhang(state);
  return mode === "side"
    ? { width: Math.max(base.width, 1 + TRAY_GAP + size.w + 2 * over.x), height: 1 }
    : { width: base.width, height: Math.max(base.height, 1 + TRAY_GAP + TRAY_LABEL_BAND + size.h + 2 * over.y) };
}

/** Tray rectangle in board units (outside the board, after the gap). */
export function trayRect(state, layout) {
  const mode = normalizeLayout(layout === undefined ? state.layout : layout);
  const bounds = surfaceBounds(mode, state);
  const origin = surfaceOrigin(state, mode);
  return mode === "side"
    ? { x: 1 + TRAY_GAP, y: 0, width: bounds.width - 1 - TRAY_GAP, height: 1 }
    : { x: origin.x, y: 1 + TRAY_GAP, width: bounds.width, height: bounds.height - 1 - TRAY_GAP };
}

function roundScore(value) {
  return Math.round(value * 1e9) / 1e9;
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

/**
 * rows × cols with near-square pieces for the image aspect ratio and a piece
 * count close to target.
 */
export function computeGrid(width, height, target, maxCols, maxRows) {
  const w = Math.max(1, Math.floor(Number(width) || 0));
  const h = Math.max(1, Math.floor(Number(height) || 0));
  const t = Math.max(MIN_SIDE * MIN_SIDE, Math.floor(Number(target) || 0));
  const colCap = clamp(
    Math.floor(maxCols === undefined ? MAX_SIDE : Number(maxCols) || 0),
    MIN_SIDE,
    MAX_SIDE
  );
  const rowCap = clamp(
    Math.floor(maxRows === undefined ? MAX_SIDE : Number(maxRows) || 0),
    MIN_SIDE,
    MAX_SIDE
  );
  const aspect = w / h;

  let best = null;
  for (let rows = MIN_SIDE; rows <= rowCap; rows++) {
    for (let cols = MIN_SIDE; cols <= colCap; cols++) {
      const pieces = rows * cols;
      const countError = Math.abs(pieces - t) / t;
      const shapeError = Math.abs(Math.log((aspect * rows) / cols));
      const score = roundScore(countError + 0.75 * shapeError);
      if (
        best === null ||
        score < best.score ||
        (score === best.score &&
          Math.abs(pieces - t) < Math.abs(best.pieces - t)) ||
        (score === best.score &&
          Math.abs(pieces - t) === Math.abs(best.pieces - t) &&
          rows < best.rows)
      ) {
        best = { rows, cols, pieces, score };
      }
    }
  }
  return { rows: best.rows, cols: best.cols, pieces: best.pieces };
}

/**
 * Column / row caps so pieces stay usable: at least MIN_PIECE_SOURCE_PX of
 * source image and (when the board size is known) MIN_PIECE_DISPLAY_PX on screen.
 */
export function gridCaps(imageWidth, imageHeight, boardWidth, boardHeight) {
  let maxCols = Math.floor((Number(imageWidth) || 0) / MIN_PIECE_SOURCE_PX);
  let maxRows = Math.floor((Number(imageHeight) || 0) / MIN_PIECE_SOURCE_PX);
  if (boardWidth > 0 && boardHeight > 0) {
    maxCols = Math.min(maxCols, Math.floor(boardWidth / MIN_PIECE_DISPLAY_PX));
    maxRows = Math.min(maxRows, Math.floor(boardHeight / MIN_PIECE_DISPLAY_PX));
  }
  return {
    maxCols: clamp(maxCols, MIN_SIDE, MAX_SIDE),
    maxRows: clamp(maxRows, MIN_SIDE, MAX_SIDE),
  };
}

/** Target piece counts per difficulty (strictly increasing, sane bounds). */
export function normalizePieceTargets(pieces) {
  const src = pieces && typeof pieces === "object" ? pieces : {};
  const out = {};
  DIFFICULTIES.forEach((level) => {
    const n = Math.floor(Number(src[level]));
    out[level] = Number.isFinite(n) && n >= 4 && n <= 200 ? n : DEFAULT_PIECES[level];
  });
  if (!(out.easy < out.medium && out.medium < out.hard)) {
    return { ...DEFAULT_PIECES };
  }
  return out;
}

/** Grid for one difficulty with image + device caps applied. */
export function gridForDifficulty(image, board, pieceTargets, difficulty) {
  const targets = normalizePieceTargets(pieceTargets);
  const level = DIFFICULTIES.indexOf(difficulty) >= 0 ? difficulty : "easy";
  const caps = gridCaps(
    image.width,
    image.height,
    board ? board.width : 0,
    board ? board.height : 0
  );
  return computeGrid(image.width, image.height, targets[level], caps.maxCols, caps.maxRows);
}

/**
 * Board pixel size: same aspect ratio as the image (never stretched), board +
 * tray fit inside the available area, never wider than MAX_BOARD_WIDTH.
 */
export function fitBoard(imageWidth, imageHeight, areaWidth, areaHeight, layout) {
  const iw = Math.max(1, Number(imageWidth) || 1);
  const ih = Math.max(1, Number(imageHeight) || 1);
  const aw = Math.max(1, Number(areaWidth) || 1);
  const ah = Math.max(1, Number(areaHeight) || 1);
  const mode = normalizeLayout(layout);
  const bounds = surfaceBounds(mode);
  const scale = Math.min(
    aw / (iw * bounds.width),
    ah / (ih * bounds.height),
    MAX_BOARD_WIDTH / iw
  );
  const width = Math.max(1, Math.floor(iw * scale));
  const height = Math.max(1, Math.floor((width * ih) / iw));
  return {
    width,
    height,
    surfaceWidth: Math.floor(width * bounds.width),
    surfaceHeight: Math.ceil(height * bounds.height),
    layout: mode,
  };
}

/**
 * Tray beside the board only on short landscape areas, and only when that
 * gives a bigger board than stacking the tray below it.
 */
export function chooseLayout(imageWidth, imageHeight, areaWidth, areaHeight, viewportWidth, viewportHeight) {
  if (!(viewportWidth > viewportHeight && viewportHeight <= 540)) return "stacked";
  const stacked = fitBoard(imageWidth, imageHeight, areaWidth, areaHeight, "stacked");
  const side = fitBoard(imageWidth, imageHeight, areaWidth, areaHeight, "side");
  return side.width > stacked.width ? "side" : "stacked";
}

function boardOfWidth(imageWidth, imageHeight, width, layout) {
  const bounds = surfaceBounds(layout);
  const height = Math.max(1, Math.floor((width * imageHeight) / imageWidth));
  return {
    width,
    height,
    surfaceWidth: Math.floor(width * bounds.width),
    surfaceHeight: Math.ceil(height * bounds.height),
    layout,
  };
}

/**
 * Board on phones: as chooseLayout + fitBoard, but when the tray goes beside
 * the board the board shrinks until the compact side tray for this
 * difficulty's grid fits the area width; the stacked layout (whose tray grows
 * downwards and scrolls) is used instead when it gives the bigger board.
 */
export function fitCompactBoard(image, areaWidth, areaHeight, viewportWidth, viewportHeight, pieceTargets, difficulty) {
  const iw = Math.max(1, Number(image.width) || 1);
  const ih = Math.max(1, Number(image.height) || 1);
  const stacked = fitBoard(iw, ih, areaWidth, areaHeight, "stacked");
  if (chooseLayout(iw, ih, areaWidth, areaHeight, viewportWidth, viewportHeight) !== "side") return stacked;
  let side = fitBoard(iw, ih, areaWidth, areaHeight, "side");
  for (let i = 0; i < 6; i++) {
    const grid = gridForDifficulty(image, side, pieceTargets, difficulty);
    const probe = { rows: grid.rows, cols: grid.cols, aspect: side.width / side.height, layout: "side", compact: true };
    const need = Math.ceil(side.width * surfaceBounds("side", probe).width - 1e-6);
    if (need <= areaWidth) return side.width > stacked.width ? side : stacked;
    const width = Math.floor((side.width * areaWidth) / need);
    if (width <= stacked.width) return stacked;
    side = boardOfWidth(iw, ih, width, "side");
  }
  return stacked;
}

/** Layout of fitCompactBoard. */
export function chooseCompactLayout(image, areaWidth, areaHeight, viewportWidth, viewportHeight, pieceTargets, difficulty) {
  return fitCompactBoard(image, areaWidth, areaHeight, viewportWidth, viewportHeight, pieceTargets, difficulty).layout;
}

/** Deterministic PRNG (mulberry32) for reproducible tests; default Math.random. */
export function seededRandom(seed) {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function pieceSize(state) {
  return { w: 1 / state.cols, h: 1 / state.rows };
}

export function homeOf(state, piece) {
  return { x: piece.col / state.cols, y: piece.row / state.rows };
}

/**
 * Tab overhang in board units: TAB_OVERHANG of the piece's shorter on-screen
 * side (state.aspect = board width / height), so tabs are round on any board.
 */
export function pieceOverhang(state) {
  const size = pieceSize(state);
  const aspect = state.aspect > 0 ? state.aspect : 1;
  const t = TAB_OVERHANG * Math.min(size.w * aspect, size.h);
  return { x: t / aspect, y: t };
}

/**
 * One shared interior edge. s = +1: the tab bulges toward the right / lower
 * neighbour (s = -1: toward the left / upper one); o = knob centre along the
 * edge (fraction), k = knob scale, d = neck skew (fraction of the short side).
 */
function randomEdge(rng) {
  return {
    s: rng() < 0.5 ? 1 : -1,
    o: 0.5 + (rng() - 0.5) * 0.08,
    k: 0.94 + rng() * 0.1,
    d: (rng() - 0.5) * 0.04,
  };
}

/**
 * The four sides of a piece: null for a flat border side, otherwise
 * { edge, tab } where edge is the shared edge object and tab is +1 when it
 * bulges out of this piece (a knob) and -1 when it cuts in (a socket).
 */
export function pieceEdges(state, piece) {
  const edges = state.edges;
  const r = piece.row;
  const c = piece.col;
  const side = (edge, outward) => (edge ? { edge, tab: edge.s * outward } : null);
  if (!edges) return { top: null, right: null, bottom: null, left: null };
  return {
    top: r > 0 ? side(edges.h[(r - 1) * state.cols + c], -1) : null,
    right: c < state.cols - 1 ? side(edges.v[r * (state.cols - 1) + c], 1) : null,
    bottom: r < state.rows - 1 ? side(edges.h[r * state.cols + c], 1) : null,
    left: c > 0 ? side(edges.v[r * (state.cols - 1) + c - 1], -1) : null,
  };
}

/** Overhang per side for a piece (flat border sides have none). */
export function pieceExtents(state, piece) {
  const over = pieceOverhang(state);
  if (!piece) return { left: over.x, right: over.x, top: over.y, bottom: over.y };
  const sides = pieceEdges(state, piece);
  return {
    left: sides.left ? over.x : 0,
    right: sides.right ? over.x : 0,
    top: sides.top ? over.y : 0,
    bottom: sides.bottom ? over.y : 0,
  };
}

function withinTolerance(state, piece, x, y, tolerance) {
  const size = pieceSize(state);
  const home = homeOf(state, piece);
  return (
    Math.abs(x - home.x) <= tolerance * size.w &&
    Math.abs(y - home.y) <= tolerance * size.h
  );
}

/**
 * Keep a piece (and its tabs) fully on the play surface. Without a piece the
 * full overhang is assumed on every side.
 */
export function clampToSurface(state, x, y, piece) {
  const size = pieceSize(state);
  const bounds = surfaceBounds(state.layout, state);
  const origin = surfaceOrigin(state, state.layout);
  const ext = pieceExtents(state, piece);
  const left = origin.x + ext.left;
  return {
    x: clamp(Number(x) || 0, left, Math.max(left, origin.x + bounds.width - size.w - ext.right)),
    y: clamp(Number(y) || 0, ext.top, Math.max(ext.top, bounds.height - size.h - ext.bottom)),
  };
}

/** Range of logical piece positions whose overhang box stays inside the tray, below its header band. */
export function trayRange(state, layout) {
  const tray = trayRect(state, layout);
  const size = pieceSize(state);
  const over = pieceOverhang(state);
  const x0 = tray.x + over.x;
  const y0 = tray.y + TRAY_LABEL_BAND + over.y;
  return {
    x0,
    y0,
    x1: Math.max(x0, tray.x + tray.width - size.w - over.x),
    y1: Math.max(y0, tray.y + tray.height - size.h - over.y),
  };
}

/**
 * Compact tray slots (one per piece, line by line): neighbours along a line
 * and between lines are at least one piece apart, so piece bodies never
 * overlap; spare tray space is spread evenly.
 */
export function traySlots(state, layout) {
  const mode = normalizeLayout(layout === undefined ? state.layout : layout);
  const pack = trayPacking(state, mode);
  const range = trayRange(state, mode);
  const along = mode === "side" ? [range.y0, range.y1] : [range.x0, range.x1];
  const across = mode === "side" ? [range.x0, range.x1] : [range.y0, range.y1];
  const slots = [];
  for (let i = 0; i < state.rows * state.cols; i++) {
    const k = i % pack.perLine;
    const line = Math.floor(i / pack.perLine);
    const a = pack.perLine > 1 ? along[0] + (k / (pack.perLine - 1)) * (along[1] - along[0]) : (along[0] + along[1]) / 2;
    const c = pack.lines > 1 ? across[0] + (line / (pack.lines - 1)) * (across[1] - across[0]) : across[0];
    slots.push(mode === "side" ? { x: c, y: a } : { x: a, y: c });
  }
  return slots;
}

function shuffle(list, rng) {
  for (let i = list.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const tmp = list[i];
    list[i] = list[j];
    list[j] = tmp;
  }
  return list;
}

/**
 * New scrambled puzzle. Every piece has exactly one home cell
 * (id = row * cols + col) and starts in the tray, away from the board. Pieces
 * are spread over a jittered grid of tray slots (they may overlap when the
 * tray is small, but never leave it). aspect = board width / height. A
 * compact puzzle puts each piece in its own tray slot instead. trayWidth
 * (board widths, stacked only) widens the tray beyond the board.
 */
export function createPuzzle(rows, cols, random, layout, aspect, compact, trayWidth) {
  const rng = typeof random === "function" ? random : Math.random;
  const r = clamp(Math.floor(rows), MIN_SIDE, MAX_SIDE);
  const c = clamp(Math.floor(cols), MIN_SIDE, MAX_SIDE);
  const mode = normalizeLayout(layout);
  const ratio = Number(aspect) > 0 ? Number(aspect) : 1;
  const state = {
    rows: r,
    cols: c,
    pieces: [],
    moves: 0,
    completed: false,
    layout: mode,
    lockOrder: [],
    aspect: ratio,
    edges: { h: [], v: [] },
  };
  if (Number(trayWidth) > 1) state.trayWidth = Number(trayWidth);
  const order = [];
  for (let i = 0; i < r * c; i++) order.push(i);
  shuffle(order, rng);
  for (let i = 0; i < (r - 1) * c; i++) state.edges.h.push(randomEdge(rng));
  for (let i = 0; i < r * (c - 1); i++) state.edges.v.push(randomEdge(rng));

  if (compact) {
    state.compact = true;
    const slots = traySlots(state, mode);
    order.forEach((id, z) => {
      state.pieces.push({ id, row: Math.floor(id / c), col: id % c, x: slots[z].x, y: slots[z].y, placed: false, z });
    });
    state.pieces.sort((a, b) => a.id - b.id);
    return state;
  }

  const size = pieceSize(state);
  const range = trayRange(state, mode);
  const spanX = range.x1 - range.x0;
  const spanY = range.y1 - range.y0;
  const n = order.length;
  const slotCols = clamp(
    Math.round(Math.sqrt((n * ((spanX + size.w) / size.w)) / ((spanY + size.h) / size.h))),
    1,
    n
  );
  const slotRows = Math.ceil(n / slotCols);
  const slots = [];
  for (let i = 0; i < slotCols * slotRows; i++) slots.push(i);
  shuffle(slots, rng);

  order.forEach((id, z) => {
    const piece = { id, row: Math.floor(id / c), col: id % c, x: 0, y: 0, placed: false, z };
    const slot = slots[z];
    const sx = slot % slotCols;
    const sy = Math.floor(slot / slotCols);
    piece.x = range.x0 + ((sx + rng()) / slotCols) * spanX;
    piece.y = range.y0 + ((sy + rng()) / slotRows) * spanY;
    state.pieces.push(piece);
  });
  state.pieces.sort((a, b) => a.id - b.id);
  return state;
}

/**
 * Same puzzle on another layout. Placed pieces, ids, moves, edges and
 * completion are unchanged; loose pieces on the board area stay put (clamped
 * inside it); loose tray pieces keep their relative spot along the tray's long
 * and short axes, so switching back and forth returns them where they were.
 * compact (omitted = unchanged) re-packs loose tray pieces into the compact
 * slots in their current slot order; so does a new trayWidth (omitted =
 * unchanged), e.g. after a window resize.
 */
export function relayoutPuzzle(state, layout, compact, trayWidth) {
  if (!state) return state;
  const from = normalizeLayout(state.layout);
  const to = normalizeLayout(layout);
  const wasCompact = !!state.compact;
  const toCompact = compact === undefined ? wasCompact : !!compact;
  const fromWidth = Number(state.trayWidth) > 1 ? Number(state.trayWidth) : 1;
  const toWidth = trayWidth === undefined ? fromWidth : Number(trayWidth) > 1 ? Number(trayWidth) : 1;
  if (from === to && wasCompact === toCompact && fromWidth === toWidth) return state;
  const size = pieceSize(state);
  const next = { ...state, layout: to };
  if (toCompact) next.compact = true;
  else delete next.compact;
  if (toWidth > 1) next.trayWidth = toWidth;
  else delete next.trayWidth;
  const inTray = (p) => (from === "side" ? p.x + size.w / 2 > 1 : p.y + size.h / 2 > 1);
  if (toCompact) {
    const slots = traySlots(next, to);
    const lineKey = (p) => (from === "side" ? [p.x, p.y] : [p.y, p.x]);
    const queue = state.pieces
      .filter((p) => !p.placed && inTray(p))
      .sort((a, b) => {
        const ka = lineKey(a);
        const kb = lineKey(b);
        return Math.abs(ka[0] - kb[0]) > 1e-9 ? ka[0] - kb[0] : Math.abs(ka[1] - kb[1]) > 1e-9 ? ka[1] - kb[1] : a.id - b.id;
      });
    const slotOf = {};
    queue.forEach((p, i) => {
      slotOf[p.id] = slots[i];
    });
    const pieces = state.pieces.map((p) => {
      if (p.placed) return p;
      if (slotOf[p.id]) return { ...p, x: slotOf[p.id].x, y: slotOf[p.id].y };
      const keepX = from === "stacked" && to === "stacked";
      const pos = clampToSurface(next, keepX ? p.x : clamp(p.x, 0, 1 - size.w), clamp(p.y, 0, 1 - size.h), p);
      return { ...p, x: pos.x, y: pos.y };
    });
    return { ...next, pieces };
  }
  const src = trayRange(state, from);
  const dst = trayRange(next, to);
  const ratio = (value, lo, hi) => (hi > lo ? clamp((value - lo) / (hi - lo), 0, 1) : 0);
  const lerp = (t, lo, hi) => lo + t * (hi - lo);
  const pieces = state.pieces.map((p) => {
    if (p.placed) return p;
    let x = p.x;
    let y = p.y;
    if (from === to && inTray(p)) {
      x = lerp(ratio(p.x, src.x0, src.x1), dst.x0, dst.x1);
      y = lerp(ratio(p.y, src.y0, src.y1), dst.y0, dst.y1);
    } else if (from === "stacked" && p.y + size.h / 2 > 1) {
      const along = ratio(p.x, src.x0, src.x1);
      const across = ratio(p.y, src.y0, src.y1);
      x = lerp(across, dst.x0, dst.x1);
      y = lerp(along, dst.y0, dst.y1);
    } else if (from === "side" && p.x + size.w / 2 > 1) {
      const along = ratio(p.y, src.y0, src.y1);
      const across = ratio(p.x, src.x0, src.x1);
      x = lerp(along, dst.x0, dst.x1);
      y = lerp(across, dst.y0, dst.y1);
    } else {
      const pos = clampToSurface(next, clamp(p.x, 0, 1 - size.w), clamp(p.y, 0, 1 - size.h), p);
      x = pos.x;
      y = pos.y;
    }
    return { ...p, x, y };
  });
  return { ...next, pieces };
}

function updatePiece(state, id, fn) {
  let changed = false;
  const pieces = state.pieces.map((p) => {
    if (p.id !== id) return p;
    const next = fn(p);
    if (next !== p) changed = true;
    return next;
  });
  return changed ? { ...state, pieces } : state;
}

function topZ(state) {
  return state.pieces.reduce((max, p) => Math.max(max, p.z), 0) + 1;
}

/** Move a loose piece (drag in progress). Locked pieces never move. */
export function movePiece(state, id, x, y) {
  if (state.completed) return state;
  const z = topZ(state);
  return updatePiece(state, id, (p) => {
    if (p.placed) return p;
    const pos = clampToSurface(state, x, y, p);
    return { ...p, x: pos.x, y: pos.y, z };
  });
}

/**
 * Drop a piece: snaps + locks into its own home cell only when within
 * tolerance; otherwise it stays where dropped.
 */
export function dropPiece(state, id, x, y, tolerance) {
  if (state.completed) return { state, snapped: false };
  const tol = tolerance === undefined ? SNAP_TOLERANCE : tolerance;
  const target = state.pieces.find((p) => p.id === id);
  if (!target || target.placed) return { state, snapped: false };
  const pos = clampToSurface(state, x, y, target);
  const snapped = withinTolerance(state, target, pos.x, pos.y, tol);
  const home = homeOf(state, target);
  let next = updatePiece(state, id, (p) => ({
    ...p,
    x: snapped ? home.x : pos.x,
    y: snapped ? home.y : pos.y,
    placed: snapped,
    z: snapped ? 0 : topZ(state),
  }));
  const lockOrder = (state.lockOrder || []).concat(snapped ? [id] : []);
  next = { ...next, moves: state.moves + 1, lockOrder };
  next = { ...next, completed: isComplete(next) };
  return { state: next, snapped };
}

/**
 * Completion proof sent to the server before sharing (grid used + the order
 * pieces locked). Null until the puzzle is complete.
 */
export function completionProof(state, difficulty) {
  if (!state || !state.completed) return null;
  return {
    difficulty,
    rows: state.rows,
    cols: state.cols,
    order: (state.lockOrder || []).slice(),
  };
}

/** Every id 0..n-1 exactly once, each with its own row/col. */
export function piecesAccountedFor(state) {
  const n = state.rows * state.cols;
  if (!state.pieces || state.pieces.length !== n) return false;
  const seen = new Set();
  return state.pieces.every((p) => {
    if (seen.has(p.id) || p.id < 0 || p.id >= n) return false;
    seen.add(p.id);
    return p.row === Math.floor(p.id / state.cols) && p.col === p.id % state.cols;
  });
}

/** Solved only when ALL pieces are locked exactly in their own cells. */
export function isComplete(state) {
  if (!piecesAccountedFor(state)) return false;
  const eps = 1e-9;
  return state.pieces.every((p) => {
    const home = homeOf(state, p);
    return p.placed && Math.abs(p.x - home.x) < eps && Math.abs(p.y - home.y) < eps;
  });
}

export function placedCount(state) {
  return state.pieces.filter((p) => p.placed).length;
}

/** Keyboard nudge step (a quarter piece). */
export function keyboardStep(state) {
  const size = pieceSize(state);
  return { dx: size.w / 4, dy: size.h / 4 };
}
